import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { filterAllTruthyInTuple, subjectFromObs } from 'core/rx-js';
import { BehaviorSubject, Subject, combineLatest, filter, map, skip, skipWhile, startWith, switchMap, take, takeUntil } from 'rxjs';
import { DynamicFormService } from '../dynamic-form.service';
import { FormControlErrorStateMatcher } from './error-state-matcher';
import { IDisposable } from './types/i-disposable';

@Injectable()
export class FormControlService<T extends any = any> implements OnDestroy {

  private _destroy = new Subject<void>()

  readonly dataFieldSubject = new BehaviorSubject<string | null>(null);
  get dataField() {
    return this.dataFieldSubject.value;
  }
  set dataField(value) {
    this.dataFieldSubject.next(value)
  }

  readonly htmlElementSubject = new BehaviorSubject<HTMLElement>(undefined);

  readonly mainFormGroupSubject = this._dynamicFormService.mainFormGroupSubject;

  readonly formControlSubject = subjectFromObs({
    initialValue: null,
    until: this._destroy,
    observable: combineLatest([
      this.dataFieldSubject,
      this.mainFormGroupSubject
    ])
      .pipe(
        filterAllTruthyInTuple(),
        map(([dataField, mainFormGroup]) => mainFormGroup.get(dataField as string))
      )
  });

  get formControl() {
    return this.formControlSubject.value;
  }
  set formControl(value) {
    this.formControlSubject.next(value);

  }

  // todo sideffects
  // set formControl(newFormControl: SSFormControl) {
  //   this._formControl = newFormControl;

  //   this.formControlSubject.next(this.formControl);
  //   this._initValidationRuleSubscription();
  //   this._pairTouchedSubjects();
  // }

  readonly touchedSubject = new BehaviorSubject<boolean>(false);
  readonly initializedSubject = new BehaviorSubject<boolean>(false);
  readonly focusedSubject = new BehaviorSubject<boolean>(false);
  readonly valueSubject = new BehaviorSubject<T | null>(null);

  readonly controlStatusObs = this.formControlSubject.pipe(
    filter(Boolean),
    switchMap(formControl => formControl.statusChanges.pipe(startWith(formControl.status))),
  )

  readonly submittedSubject = this._dynamicFormService.submittedSubject;

  readonly brokenRulesSubject = subjectFromObs({
    observable: combineLatest([
      this._dynamicFormService.applyValidationSubject,
      this.controlStatusObs,
      this.touchedSubject,
      this._dynamicFormService.submittedSubject,
    ])
      .pipe(
        filter(([, _, touched, submitted]) => touched || submitted),
        map(([applyValidation]) => applyValidation ? this.formControl?.getBrokenRules() : [])
      ),
    initialValue: null,
    until: this._destroy
  })

  readonly validationMessageSubject = this.brokenRulesSubject
    .pipe(
      map((brokenRules) => {
        if (!brokenRules) {
          return '';
        }

        const validationMessage = brokenRules
          .map(({ message, type }) =>
            message ||
            this._translateService.instant('ss-validation.' + type)
          )
          .join('\n');

        return validationMessage;
      })
    )

  readonly validSubject = subjectFromObs({
    initialValue: true,
    observable: this.brokenRulesSubject.pipe(map((brokenRules) => !brokenRules?.length)),
    until: this._destroy
  })

  readonly requiredObs = this.formControlSubject
    .pipe(
      filter(Boolean),
      map((formControl) => !!formControl.validationRules?.find(({ type }) => type === 'required'))
    )


  readonly errorMatcher: FormControlErrorStateMatcher = new FormControlErrorStateMatcher(() => this);

  readonly readOnlyObs = this.formControlSubject.pipe(
    map((formControl) => formControl?.readOnly === true)
  )

  readonly labelObs = this.formControlSubject.pipe(
    filter(Boolean),
    map((formControl) => formControl.label)
  )


  readonly actionSubject = subjectFromObs({
    initialValue: null,
    observable: this.formControlSubject.pipe(
      filter(Boolean),
      switchMap(({ actionSubject }) => actionSubject)
    ),
    until: this._destroy
  })

  get action() {
    return this.actionSubject.value
  }

  constructor(
    private _dynamicFormService: DynamicFormService,
    private _translateService: TranslateService
  ) {

    combineLatest([
      this.valueSubject.pipe(skip(1)),
      this.formControlSubject.pipe(filter(Boolean)),
    ])
      .pipe(
        takeUntil(this._destroy),
        skipWhile(([value, formControl]) => (![null, undefined].includes(formControl.defaultValue) || formControl.value === value)),
        filter(([value, formControl]) => formControl.value !== value),
        takeUntil(this._destroy)
      )
      .subscribe(([value, formControl]) => formControl.setValue(value))

    this.formControlSubject.pipe(
      filter(Boolean),
      takeUntil(this._destroy)
    )
      .subscribe((formControl) => {
        formControl.controlService = this;
      })

    this.formControlSubject.pipe(filter(Boolean), take(1), takeUntil(this._destroy))
      .subscribe(formControl => {


        if (formControl.initialValue !== null && formControl.initialValue !== undefined) {

          this.valueSubject.next(formControl.initialValue);
        }
      })

    this.formControlSubject.pipe(
      filter(Boolean),
      switchMap((formControl) => formControl.valueChanges.pipe(startWith(formControl.value))),
      filter((value) => value !== this.valueSubject.value),
      takeUntil(this._destroy)
    )
      .subscribe(value => {
        this.valueSubject.next(value)
      })
  }


  private readonly _disposables: IDisposable[] = [];
  ngOnDestroy(): void {

    this._destroy.next();
    this._destroy.complete();

    this._disposables.forEach((disposable) => disposable.dispose());

  }

  focus() {

    this.focusedSubject.next(true);
    this.htmlElementSubject
      .pipe(filter(Boolean), take(1), takeUntil(this._destroy))
      .subscribe(htmlElement => {

        htmlElement.autofocus = true
        htmlElement.focus();
        setTimeout(() => htmlElement.autofocus = false, 1000)
      })
  }

  executeActionCallback() {
    this.action.callback(this.formControl);
  }

}
